import React from 'react';
import { IonCard, IonCardContent, IonCardHeader, IonCardTitle, IonButton } from '@ionic/react';
import { createUseStyles } from 'react-jss';
import { Text } from '../../components/Text';
import { MessageScenario, ITaskStatusContext } from '@innerhive-internal/innerhive-common';
import { getTaskWrapUpContent } from './taskWrapUpUtils';
import { TaskWrapUpAction } from './taskWrapUpTypes';

interface TaskWrapUpCardProps {
    scenario: MessageScenario;
    context: ITaskStatusContext;
    onAction?: (action: string) => void;
    onAddTask?: () => void;
}

const useStyles = createUseStyles({
    eveningCard: {
        margin: '16px',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        '--ion-card-background': 'white',
        border: 'none'
    },

    cardHeader: {
        borderBottomWidth: 0,
        padding: '20px 16px 12px 16px'
    },

    cardTitle: {
        fontSize: '20px',
        fontWeight: '700',
        color: 'var(--ion-color-dark, #032A37)',
        margin: '0 0 8px 0'
    },

    cardContent: {
        padding: '0 16px 20px 16px',
        '--padding-start': 0,
        '--padding-end': 0,
        '--padding-top': 0,
        '--padding-bottom': 0
    },

    message: {
        fontSize: '16px',
        color: 'var(--ion-color-medium, #687f87)',
        lineHeight: '1.5',
        margin: '0 0 12px 0'
    },

    subMessage: {
        fontSize: '14px',
        color: 'var(--ion-color-step-500)',
        fontStyle: 'italic',
        margin: '12px 0 0 0'
    },

    actionsContainer: {
        display: 'grid',
        gridTemplateColumns: '1fr',
        gap: '12px',
        marginTop: '20px'
    },

    solidButton: {
        '--background': 'var(--ion-color-primary)',
        '--color': 'white',
        '--border-radius': '8px',
        '--padding-top': '12px',
        '--padding-bottom': '12px',
        '--padding-start': '16px',
        '--padding-end': '16px',
        fontSize: '14px',
        fontWeight: '600',
        textTransform: 'none',
        letterSpacing: 'normal',
        margin: 0
    },

    outlineButton: {
        '--background': 'transparent',
        '--border-color': 'var(--ion-color-primary)',
        '--color': 'var(--ion-color-primary)',
        '--border-radius': '8px',
        '--padding-top': '12px',
        '--padding-bottom': '12px',
        '--padding-start': '16px',
        '--padding-end': '16px',
        fontSize: '14px',
        fontWeight: '600',
        textTransform: 'none',
        letterSpacing: 'normal',
        margin: 0
    }
});

export const TaskWrapUpCard: React.FC<TaskWrapUpCardProps> = ({
    scenario,
    context,
    onAction,
    onAddTask
}) => {
    const classes = useStyles();
    const content = getTaskWrapUpContent(scenario, context);

    const handleAction = (action: string) => {
        if (action === 'add-task-tomorrow' && onAddTask) {
            onAddTask();
        } else if (onAction) {
            onAction(action);
        }
    };

    return (
        <IonCard
            className={classes.eveningCard}
            data-id={`task-wrap-up-card-${scenario}`}
        >
            <IonCardHeader className={classes.cardHeader}>
                <IonCardTitle className={classes.cardTitle} data-id="wrap-up-card-title">
                    {content.title}
                </IonCardTitle>
            </IonCardHeader>

            <IonCardContent className={classes.cardContent}>
                {/* TODO: Review Text component wrapper - wrapped in div for className support since Text doesn't accept className prop */}
                <div className={classes.message} data-id="wrap-up-card-message">
                    <Text type="normal">
                        {content.message}
                    </Text>
                </div>

                {content.subMessage && (
                    <p className={classes.subMessage} data-id="wrap-up-card-submessage">
                        {content.subMessage}
                    </p>
                )}

                {content.actions && content.actions.length > 0 && (
                    <div className={classes.actionsContainer} data-id="wrap-up-card-actions">
                        {content.actions.map((action: TaskWrapUpAction, index: number) => (
                            <IonButton
                                key={action.action}
                                className={index === 0 ? classes.solidButton : classes.outlineButton}
                                fill={index === 0 ? 'solid' : 'outline'}
                                onClick={() => handleAction(action.action)}
                                expand="block"
                                data-id={`wrap-up-action-${action.action}`}
                            >
                                {action.label}
                            </IonButton>
                        ))}
                    </div>
                )}
            </IonCardContent>
        </IonCard>
    );
};
